import { ApiService } from 'app/api';
import { IApplicationContext } from 'app/context/types';
import { LessonType, Region } from '../constants/enums';
import {
	IBusyPictureLesson,
	ICreateBusyPictureLesson,
} from '../interfaces/busyPictureLesson';
import {
	ICreateGrammarLesson,
	IGrammarLesson,
} from '../interfaces/grammarLesson';
import { ICreateLesson, ILesson } from '../interfaces/lesson';
import {
	ICreateLessonCategory,
	ILessonCategory,
} from '../interfaces/lessonCategory';
import { ICreateLessonItem, ILessonItem } from '../interfaces/lessonItem';
import {
	ICreateLessonItemModifier,
	ILessonItemModifier,
} from '../interfaces/lessonItemModifier';
import {
	ICreateLexicaryLesson,
	ILexicaryLesson,
} from '../interfaces/lexicaryLesson';
import {
	ICreateNumberCombinationLesson,
	INumberCombinationLesson,
} from '../interfaces/numberCombinationLesson';
import {
	ICreateSceneMatchingLesson,
	ISceneMatchingLesson,
} from '../interfaces/sceneMatchingLesson';
import {
	ICreateSentenceLesson,
	ISentenceLesson,
} from '../interfaces/sentenceLesson';
import {
	ICreateSightWordsLesson,
	ISightWordsLesson,
} from '../interfaces/sightWordsLesson';
import {
	ICreateSortingLesson,
	ISortingLesson,
} from '../interfaces/sortingLesson';
import { ICreateTimeLesson, ITimeLesson } from '../interfaces/timeLesson';
import { ICreateVerbLesson, IVerbLesson } from '../interfaces/verbLesson';
import { ICreateVocabLesson, IVocabLesson } from '../interfaces/vocabLesson';

type GetLessonsPayload = {
	page?: number;
	lessonsRegion: Region;
	lessonNameSearchKey?: string;
	lessonType?: number;
	categorySearchKey?: string;
};

type AutoCompleteCondition = 0 | -1 | 1;

export class RegionsConfigurationService {
	private apiService: ApiService;

	constructor(private applicationContext: IApplicationContext) {
		this.apiService = new ApiService(this.applicationContext);
	}

	getLessons(payload: GetLessonsPayload) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_LESSONS,
					{},
					{
						pathParams: this.getGetLessonsPathParams(payload),
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	searchLessonsAutoComplete({
		lessonsRegion,
		lessonNameSearchKey,
		hasCulureCard,
		hasLiveSessionCard,
		hasModuleItem,
	}: {
		lessonsRegion: Region;
		lessonNameSearchKey: string;
		hasCulureCard?: AutoCompleteCondition;
		hasLiveSessionCard?: AutoCompleteCondition;
		hasModuleItem?: AutoCompleteCondition;
	}) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.SEARCH_LESSONS_AUTO_COMPLETE,
					{},
					{
						pathParams: {
							lessonNameSearchKey,
							lessonsRegion,
							hasCulureCard: hasCulureCard === 0 ? 0 : hasCulureCard || -1,
							hasLiveSessionCard:
								hasLiveSessionCard === 0 ? 0 : hasLiveSessionCard || -1,
							hasModuleItem: hasModuleItem === 0 ? 0 : hasModuleItem || -1,
						},
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	deleteLesson(id: number, type: LessonType) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.DELETE_LESSON,
					{},
					{
						reqPathParam: `${this.getUriPrefixByLessonType(type)}/${id}`,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getRegionNewLessonDefaultOrder(region: Region) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_REGION_NEW_LESSON_DEFAULT_ORDER,
					{},
					{
						reqPathParam: region.toString(),
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createVocabLesson(newLessonData: IVocabLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_VOCAB_LESSON,
					this.formatVocabLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateVocabLesson(lessonData: IVocabLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_VOCAB_LESSON,
					this.formatVocabLessonReqData(lessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getVocabLesson(id: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_VOCAB_LESSON,
					{},
					{
						reqPathParam: id,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createVerbLesson(newLessonData: IVerbLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_VERB_LESSON,
					this.formatVerbLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateVerbLesson(lessonData: IVerbLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_VERB_LESSON,
					this.formatVerbLessonReqData(lessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getVerbLesson(id: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_VERB_LESSON,
					{},
					{
						reqPathParam: id,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getSentencesWithVocabLessonWord(vocabWordId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_SENTENCE_LESSONS_WITH_VOCAB_WORD,
					{},
					{
						reqPathParam: vocabWordId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getSentencesWithVocabLessonWords(vocabLessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_SENTENCE_LESSONS_WITH_VOCAB_LESSON_WORDS,
					{},
					{
						reqPathParam: vocabLessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getSentenceLessonsWithOrderLessThanVocabLessonNewOrder(
		vocabLessonId: number,
		newOrder: number,
	) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_SENTENCE_LESSONS_WITH_ORDER_LESS_THAN_VOCAB_LESSON_NEW_ORDER,
					{},
					{
						reqPathParam: `${vocabLessonId}/${newOrder}`,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getSentenceLesson(sentenceLessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_SENTENCE_LESSON,
					{},
					{
						reqPathParam: sentenceLessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createSentenceLesson(newLessonData: ISentenceLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_SENTENCE_LESSON,
					this.formatSentenceLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateSentenceLesson(newLessonData: ISentenceLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_SENTENCE_LESSON,
					this.formatSentenceLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	searchSentenceLessonVocabWords(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.SEARCH_SENTENCE_LESSON_VOCAB_WORDS,
					{},
					{
						pathParams: {
							searchKey,
						},
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getLexicaryLesson(lexicaryLessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_LEXICARY_LESSON,
					{},
					{
						reqPathParam: lexicaryLessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createLexicaryLesson(newLessonData: ILexicaryLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_LEXICARY_LESSON,
					this.formatLexicaryLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateLexicaryLesson(newLessonData: ILexicaryLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_LEXICARY_LESSON,
					this.formatLexicaryLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	searchLessonItemModifiers(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.SEARCH_LESSON_ITEM_MODIFIERS,
					null,
					{
						pathParams: { searchKey },
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	createLessonItemModifier(tag: ILessonItemModifier) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_LESSON_ITEM_MODIFIER,
					this.formatLessonItemModifierData(tag),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	updateLessonItemModifier(tag: ILessonItemModifier) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_LESSON_ITEM_MODIFIER,
					this.formatLessonItemModifierData(tag),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	deleteLessonItemModifier(id: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.DELETE_LESSON_ITEM_MODIFIER,
					null,
					{
						reqPathParam: id,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	getGrammarLesson(grammarLessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_GRAMMAR_LESSON,
					{},
					{
						reqPathParam: grammarLessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createGrammarLesson(newLessonData: IGrammarLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_GRAMMAR_LESSON,
					this.formatGrammarLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateGrammarLesson(newLessonData: IGrammarLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_GRAMMAR_LESSON,
					this.formatGrammarLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getSortingLesson(sortingLessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_SORTING_LESSON,
					{},
					{
						reqPathParam: sortingLessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createSortingLesson(newLessonData: ISortingLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_SORTING_LESSON,
					this.formatSortingLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateSortingLesson(newLessonData: ISortingLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_SORTING_LESSON,
					this.formatSortingLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getBusyPictureLesson(sortingLessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_BUSY_PICTURE_LESSON,
					{},
					{
						reqPathParam: sortingLessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createBusyPictureLesson(newLessonData: IBusyPictureLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_BUSY_PICTURE_LESSON,
					this.formatBusyPictureLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateBusyPictureLesson(newLessonData: IBusyPictureLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_BUSY_PICTURE_LESSON,
					this.formatBusyPictureLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getNumberCombinationLesson(lessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_NUMBER_COMBINATION_LESSON,
					{},
					{
						reqPathParam: lessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createNumberCombinationLesson(newLessonData: INumberCombinationLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_NUMBER_COMBINATION_LESSON,
					this.formatNumberCombinationLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateNumberCombinationLesson(newLessonData: INumberCombinationLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_NUMBER_COMBINATION_LESSON,
					this.formatNumberCombinationLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getSightWordsLesson(lessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_SIGHT_WORDS_LESSON,
					{},
					{
						reqPathParam: lessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createSightWordsLesson(newLessonData: ISightWordsLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_SIGHT_WORDS_LESSON,
					this.formatSightWordsLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateSightWordsLesson(newLessonData: ISightWordsLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_SIGHT_WORDS_LESSON,
					this.formatSightWordsLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getTimeLesson(lessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_TIME_LESSON,
					{},
					{
						reqPathParam: lessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createTimeLesson(newLessonData: ITimeLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_TIME_LESSON,
					this.formatTimeLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateTimeLesson(newLessonData: ITimeLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_TIME_LESSON,
					this.formatTimeLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getSceneMatchingLesson(lessonId: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_SCENE_MATCHING_LESSON,
					{},
					{
						reqPathParam: lessonId,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	createSceneMatchingLesson(newLessonData: ISceneMatchingLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_SCENE_MATCHING_LESSON,
					this.formatSceneMatchingLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	updateSceneMatchingLesson(newLessonData: ISceneMatchingLesson) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_SCENE_MATCHING_LESSON,
					this.formatSceneMatchingLessonReqData(newLessonData),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	getLessonCategories(page: number, searchKey: string, id?: number) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.GET_CATEGORIES,
					null,
					{
						pathParams: {
							page,
							...(searchKey ? { searchKey } : {}),
							...(id ? { id } : {}),
						},
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	createLessonCategory(newLessonCategory: ILessonCategory) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.CREATE_CATEGORY,
					this.formatLessonCategoryData(newLessonCategory),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	updateLessonCategory(guide: ILessonCategory): Promise<ILessonCategory> {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.UPDATE_CATEGORY,
					this.formatLessonCategoryData(guide),
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	deleteLessonCategory(id: number) {
		return this.apiService.invoke(
			this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
				.DELETE_CATEGORY,
			null,
			{ reqPathParam: id },
		);
	}

	searchLessonCategories(searchKey: string) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.SEARCH_CATEGORIES,
					null,
					{
						pathParams: {
							searchKey,
						},
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((error: Error) => {
					reject(error);
				}),
		);
	}

	searchVocabWordsAutoComplete(pathParams: {
		searchKey: string;
		lessonRegion: Region;
		lessonOrder: number;
	}) {
		return new Promise((resolve, reject) =>
			this.apiService
				.invoke(
					this.applicationContext.getApiConfig().REGIONS_CONFIGURATION
						.SEARCH_VOCAB_WORDS_FROM_PREVIOUS_LESSONS,
					{},
					{
						pathParams,
					},
				)
				.then(({ data }: any) => {
					resolve(data);
				})
				.catch((err: Error) => {
					reject(err);
				}),
		);
	}

	private formatLexicaryLessonReqData({
		phases,
		...lesson
	}: ILexicaryLesson): ICreateLexicaryLesson {
		return {
			...this.formatLessonReqData(lesson),
			phases: phases.map(phase => ({
				...(phase.id ? { id: phase.id } : {}),
				name: phase.name,
				order: phase.order,
				videoId: phase.video.id,
				phrases: phase.phrases.map(phrase => ({
					...(phrase.id ? { id: phrase.id } : {}),
					videoId: phrase.video.id,
					voiceId: phrase.voice.id,
				})),
			})),
		};
	}

	private formatSentenceLessonReqData(
		lesson: ISentenceLesson,
	): ICreateSentenceLesson {
		return {
			...this.formatLessonReqData(lesson),
			...(lesson.sentence ? { sentenceId: lesson.sentence.id } : {}),
		};
	}

	private formatVerbLessonReqData(verbLesson: IVerbLesson): ICreateVerbLesson {
		return {
			...this.formatLessonReqData(verbLesson),
			pronouns: verbLesson.pronouns,
			verbs: verbLesson.verbs.map(verb => ({
				...this.formatLessonItemData(verbLesson, verb),
				animationVideoId: verb.animationVideo.id,
				pronounsVoices: verb.pronounsVoices.map(pronounVoice => ({
					...(pronounVoice.id ? { id: pronounVoice.id } : {}),
					pronoun: pronounVoice.pronoun,
					teachingAudioId: pronounVoice.teachingAudio.id,
					questionAudioId: pronounVoice.questionAudio.id,
					correctAnswerAudioId: pronounVoice.correctAnswerAudio.id,
					comboImageId: pronounVoice.comboImage?.id,
				})),
			})),
		};
	}

	private formatVocabLessonReqData(
		vocabLesson: IVocabLesson,
	): ICreateVocabLesson {
		return {
			...this.formatLessonReqData(vocabLesson),
			teachWordsByOrder: vocabLesson.teachWordsByOrder,
			words: vocabLesson.words.map(word => ({
				...this.formatLessonItemData(vocabLesson, word),
				nameEng: word.nameEng,
				imageId: word.image.id,
				teachingAudioId: word.teachingAudio.id,
				questionAudioId: word.questionAudio.id,
				correctAnswerAudioId: word.correctAnswerAudio.id,
			})),
		};
	}

	private formatSightWordsLessonReqData(
		sightWordsLesson: ISightWordsLesson,
	): ICreateSightWordsLesson {
		return {
			...this.formatLessonReqData(sightWordsLesson),
			teachWordsByOrder: sightWordsLesson.teachWordsByOrder,
			sightWords: sightWordsLesson.sightWords.map(word => ({
				...(word.id ? { id: word.id } : {}),
				order: word.order,
				vocabWordId: word.vocabWord.id,
			})),
		};
	}

	private formatLessonCategoryData(
		lessonCategory: ILessonCategory,
	): ICreateLessonCategory {
		return {
			...(lessonCategory.id ? { id: lessonCategory.id } : {}),
			title: lessonCategory.title,
			imageId: lessonCategory.image.id,
		};
	}

	private formatLessonItemData(
		_: ILesson,
		lessonItem: ILessonItem,
	): ICreateLessonItem {
		const categoriesIds = lessonItem.categories.map(
			category => category.id as number,
		);

		return {
			...(lessonItem.id ? { id: lessonItem.id } : {}),
			name: lessonItem.name,
			categoriesIds,
		};
	}

	private formatLessonReqData({
		id,
		description,
		name,
		order,
		numberOfGems,
		introVideo,
		region,
		category,
		enablePauseIntroVideoAtEnd,
		isSkippable,
		excludeWordsFromReview,
	}: ILesson): ICreateLesson {
		return {
			id,
			name,
			order,
			numberOfGems,
			description,
			region,
			enablePauseIntroVideoAtEnd,
			introVideoId: introVideo?.id,
			categoryId: category?.id,
			isSkippable,
			excludeWordsFromReview,
		};
	}

	private getUriPrefixByLessonType(type: LessonType): string {
		return {
			[LessonType.VERB]: 'verb',
			[LessonType.LEXICAL]: 'lexicary',
			[LessonType.VOCAB]: 'vocab',
			[LessonType.SENTENCE]: 'sentence',
			[LessonType.GRAMMAR]: 'grammar',
			[LessonType.SORTING]: 'sorting',
			[LessonType.BUSY_PICTURE]: 'busy-picture',
			[LessonType.NUMBER_COMBINATION]: 'number-combination',
			[LessonType.SIGHT_WORDS]: 'sight-words',
			[LessonType.TIME]: 'time',
			[LessonType.SCENE_MATCHING]: 'scene-matching',
		}[type];
	}

	private getGetLessonsPathParams(payload: GetLessonsPayload) {
		let pathParams: any = {
			page: payload.page || 1,
			lessonsRegion: payload.lessonsRegion,
		};
		if (payload.lessonNameSearchKey) {
			pathParams['lessonNameSearchKey'] = payload.lessonNameSearchKey;
		}
		if (
			(payload.lessonType || payload.lessonType === 0) &&
			payload.lessonType !== -1
		) {
			pathParams['lessonType'] = payload.lessonType;
		}

		if (payload.categorySearchKey) {
			pathParams['categorySearchKey'] = payload.categorySearchKey;
		}

		return pathParams;
	}

	private formatLessonItemModifierData({
		id,
		name,
		image,
	}: ILessonItemModifier): ICreateLessonItemModifier {
		return {
			id,
			name,
			imageId: image.id,
		};
	}

	private formatGrammarLessonReqData(
		grammarLesson: IGrammarLesson,
	): ICreateGrammarLesson {
		return {
			...this.formatLessonReqData(grammarLesson),
			objects: grammarLesson.objects.map(obj => ({
				...this.formatLessonItemData(grammarLesson, obj),
				imageId: obj.image.id,
				modifiersVoices: obj.modifiersVoices.map(modifierVoices => ({
					...(modifierVoices.id ? { id: modifierVoices.id } : {}),
					modifierId: modifierVoices.modifierId,
					teachingAudioId: modifierVoices.teachingAudio.id,
					questionAudioId: modifierVoices.questionAudio.id,
					correctAnswerAudioId: modifierVoices.correctAnswerAudio.id,
					comboImageId: modifierVoices.comboImage?.id,
				})),
			})),
		};
	}
	private formatSortingLessonReqData(
		sortingLesson: ISortingLesson,
	): ICreateSortingLesson {
		return {
			...this.formatLessonReqData(sortingLesson),
			subjects: sortingLesson.subjects.map(sub => ({
				...this.formatLessonItemData(sortingLesson, sub),
				imageId: sub.image.id,
				words: sub.words.map(word => ({
					...(word.id ? { id: word.id } : {}),
					name: word.name,
					voiceId: word.voice.id,
				})),
			})),
		};
	}

	private formatBusyPictureLessonReqData(
		lesson: IBusyPictureLesson,
	): ICreateBusyPictureLesson {
		return {
			...this.formatLessonReqData(lesson),
			teachWordsByOrder: lesson.teachWordsByOrder,
			imageId: lesson.image.id,
			questions: lesson.questions.map(question => ({
				...(question.id ? { id: question.id } : {}),
				name: question.name,
				type: question.type,
				order: question.order,
				questionAudioId: question.questionAudio.id,
				...(question.correctAudio
					? { correctAudioId: question.correctAudio.id }
					: {}),
				...(question.imageAreaRectCoordinates
					? { imageAreaRectCoordinates: question.imageAreaRectCoordinates }
					: {}),
			})),
		};
	}

	private formatNumberCombinationLessonReqData(
		lesson: INumberCombinationLesson,
	): ICreateNumberCombinationLesson {
		return {
			...this.formatLessonReqData(lesson),
			rangeMin: lesson.rangeMin,
			rangeMax: lesson.rangeMax,
			questionsLength: lesson.questionsLength,
		};
	}

	private formatTimeLessonReqData(lesson: ITimeLesson): ICreateTimeLesson {
		return {
			...this.formatLessonReqData(lesson),
			level: lesson.level,
			questionsLength: lesson.questionsLength,
		};
	}

	private formatSceneMatchingLessonReqData(
		lesson: ISceneMatchingLesson,
	): ICreateSceneMatchingLesson {
		return {
			...this.formatLessonReqData(lesson),
			scenesGroupId: lesson.scenesGroup?.id as number,
		};
	}
}
